import { useEffect, useMemo, useRef, useState } from 'react';

import { useRouteLoaderData } from '@remix-run/react';
import type { Price, PricingFormMode, Tier, TPricingAttributes } from '@shared/dream-components';
import { PRICING_ELEMENT_DEFAULT_INPUT_BORDER_RADIUS } from '@shared/dream-components';
import type { StripeElementsOptions } from '@stripe/stripe-js';

import useEmail from '~/hooks/useEmail';
import useStripeSession from '~/hooks/useStripeSession';
import { PriceInterval } from '~/interfaces/subscriptions';
import type { loader } from '~/root';

import { UpgradeContext } from './UpgradeContext';

interface UpgradeProviderProps {
  children: React.ReactNode;
  tiers: Tier[];
  giftTiers: Tier[];
  isInternalCheckout: boolean;
  recommendedTierId?: string;
  recommendedPriceId?: string;
  isGiftingEnabled: boolean;
  isGiftingOnly: boolean;
  nodeAttributes: TPricingAttributes;
}

const getSelectedTierAndPrice = (tiers?: Tier[], recommendedTierId?: string, recommendedPriceId?: string) => {
  if (!tiers) return {};
  if (Array.isArray(tiers) && tiers.length === 0) return {};

  // If there's a recommended price ID but no tier ID, search across all tiers for that price
  if (recommendedPriceId && !recommendedTierId) {
    for (const tier of tiers) {
      const recommendedPrice = tier.prices.find((price) => price.id === recommendedPriceId);
      if (recommendedPrice) {
        return {
          selectedTier: tier,
          selectedPrice: recommendedPrice,
        };
      }
    }
  }

  // If there's a recommended tier ID, try to find it
  if (recommendedTierId) {
    const recommendedTier = tiers.find((tier) => tier.id === recommendedTierId);
    if (recommendedTier) {
      // If there's a recommended price ID, try to find it within the recommended tier
      if (recommendedPriceId) {
        const recommendedPrice = recommendedTier.prices.find((price) => price.id === recommendedPriceId);
        if (recommendedPrice) {
          return {
            selectedTier: recommendedTier,
            selectedPrice: recommendedPrice,
          };
        }
      }

      // If no specific price is recommended, use the first price of the recommended tier
      if (Array.isArray(recommendedTier.prices) && recommendedTier.prices.length > 0) {
        return {
          selectedTier: recommendedTier,
          selectedPrice: recommendedTier.prices[0],
        };
      }

      return { selectedTier: recommendedTier };
    }
  }

  // Fallback to first tier if no recommended tier is found or specified
  const firstTier = tiers[0];
  if (Array.isArray(firstTier.prices) && firstTier.prices.length === 0) return { selectedTier: firstTier };

  return {
    selectedTier: firstTier,
    selectedPrice: firstTier.prices[0],
  };
};

const toStr = (v: unknown): string => (typeof v === 'string' ? v : '');

export const UpgradeProvider: React.FC<UpgradeProviderProps> = ({
  children,
  tiers,
  giftTiers,
  isInternalCheckout,
  recommendedTierId,
  recommendedPriceId,
  isGiftingEnabled,
  isGiftingOnly,
  nodeAttributes,
}) => {
  const { publication, isLoggedIn } = useRouteLoaderData<typeof loader>('root');

  // Only load Stripe if there are non-free tiers
  const shouldLoadStripe = useMemo(() => tiers.some((tier) => tier.id !== 'free'), [tiers]);
  const { stripeSession, isLoadingStripeSession } = useStripeSession({
    publication,
    shouldLoadStripe,
  });

  const [email, setEmail] = useEmail();
  const [taxId, setTaxId] = useState('');
  const [donationAmountCents, setDonationAmountCents] = useState(0);
  const [selectedTier, setSelectedTier] = useState<Tier | undefined>();
  const [selectedPrice, setSelectedPrice] = useState<Price | undefined>();
  const selectedTierRef = useRef(selectedTier);
  const selectedPriceRef = useRef(selectedPrice);
  selectedTierRef.current = selectedTier;
  selectedPriceRef.current = selectedPrice;

  const [selectedGiftTier, setSelectedGiftTier] = useState<Tier | undefined>();
  const [selectedGiftPrice, setSelectedGiftPrice] = useState<Price | undefined>();
  const [currentFormMode, setCurrentFormMode] = useState<PricingFormMode>(
    isGiftingEnabled && isGiftingOnly ? 'gift' : 'individual',
  );

  // Set tier/price first time, and refresh when tiers data changes (e.g. after API fetch enriches offers)
  useEffect(() => {
    const currentTier = selectedTierRef.current;
    const currentPrice = selectedPriceRef.current;

    const { selectedTier: resolvedTier, selectedPrice: resolvedPrice } = getSelectedTierAndPrice(
      tiers,
      recommendedTierId,
      recommendedPriceId,
    );

    if (!currentTier && !currentPrice) {
      // First time: set initial selection
      if (resolvedTier) setSelectedTier(resolvedTier);
      setSelectedPrice(resolvedPrice || resolvedTier?.prices[0]);
    } else if (currentTier && currentPrice) {
      // Tiers data changed: refresh selected price from new tiers to pick up enriched premium_offer data
      const updatedTier = tiers.find((t) => t.id === currentTier.id);
      if (updatedTier) {
        const updatedPrice = updatedTier.prices.find((p) => p.id === currentPrice.id);
        if (updatedPrice) {
          setSelectedTier(updatedTier);
          setSelectedPrice(updatedPrice);
        }
      }
    }
  }, [tiers, recommendedTierId, recommendedPriceId]);

  // Set gift tier/price first time
  useEffect(() => {
    if (selectedGiftTier || selectedGiftPrice) return;

    const { selectedTier: initialSelectedGiftTier, selectedPrice: initialSelectedGiftPrice } = getSelectedTierAndPrice(
      giftTiers,
      recommendedTierId,
      recommendedPriceId,
    );

    if (initialSelectedGiftTier && initialSelectedGiftPrice) {
      setSelectedGiftTier(initialSelectedGiftTier);
      setSelectedGiftPrice(initialSelectedGiftPrice);
    } else if (initialSelectedGiftTier) {
      setSelectedGiftTier(initialSelectedGiftTier);
      setSelectedGiftPrice(initialSelectedGiftTier.prices[0]);
    }
  }, [selectedGiftPrice, selectedGiftTier, giftTiers, recommendedTierId, recommendedPriceId]);

  const isStripeLoading = isLoadingStripeSession;

  const appearanceRules = useMemo((): Record<string, Record<string, string>> => {
    const n = nodeAttributes;
    return {
      '.Block': {
        backgroundColor: toStr(n.inputTextColor),
      },
      '.Tab': {
        backgroundColor: toStr(n.cardBackgroundColor),
        color: toStr(n.nameColor),
        padding: '8px',
        borderStyle: 'solid',
        borderWidth: '1px',
        borderColor: toStr(n.cardBorderColor),
      },
      '.Tab:hover': {
        backgroundColor: toStr(n.cardBackgroundColor),
        color: toStr(n.nameColor),
      },
      '.TabIcon': {
        fill: toStr(n.nameColor),
      },
      '.Tab--selected': {
        backgroundColor: toStr(n.cardSelectedBackgroundColor),
        color: toStr(n.nameSelectedColor),
        borderStyle: 'solid',
        borderWidth: '1px',
        borderColor: toStr(n.cardSelectedBorderColor),
      },
      '.Tab--selected:hover': {
        backgroundColor: toStr(n.cardSelectedBackgroundColor),
        color: toStr(n.nameSelectedColor),
      },
      '.TabIcon--selected': {
        fill: toStr(n.nameSelectedColor),
      },
      '.Label': {
        color: toStr(n.inputPlaceholderColor),
      },
      '.Label--resting': {
        fontSize: '14px',
      },
      '.Input': {
        padding: '12px 16px',
        color: toStr(n.inputTextColor),
        backgroundColor: toStr(n.inputBackgroundColor),
        fontSize: '14px',
        borderStyle: toStr(n.inputBorderStyle),
        borderWidth: toStr(n.inputBorderWidth),
        borderColor: toStr(n.inputBorderColor),
        borderRadius: toStr(n.inputBorderRadius),
      },
      '.Input::placeholder': {
        color: toStr(n.inputPlaceholderColor),
        fontSize: '14px',
      },
      '.Input--invalid': {
        outline: '0',
        borderStyle: toStr(n.inputBorderStyle),
        borderWidth: toStr(n.inputBorderWidth),
        borderColor: toStr(n.inputBorderColor),
        borderRadius: toStr(n.inputBorderRadius),
        boxShadow: 'none',
      },
      '.Error': {
        color: toStr(n.inputTextColor),
        backgroundColor: toStr(n.inputBackgroundColor),
        padding: '12px 16px',
        borderRadius: '6px',
        fontSize: '12px',
      },
    };
  }, [nodeAttributes]);

  const stripeElementsOptions: StripeElementsOptions | undefined = useMemo(() => {
    if (!isInternalCheckout) return undefined;

    const price = currentFormMode === 'gift' ? selectedGiftPrice : selectedPrice;
    if (!price) return undefined;

    const baseOptions: StripeElementsOptions = {
      // If we wanted to manually specify the payment methods to display
      // we would use paymentMethodTypes: ['card', 'apple_pay', 'google_pay', '...']
      // The same happens in the backend when creating a setup intent in
      // Stripe::OrchidUpgrades::CreateSubscription#payment_settings
      paymentMethodTypes: ['card'],
      currency: price.currency,
      amount: price.amount_cents,
      locale: publication?.language,
      appearance: {
        theme: 'flat' as const,
        labels: 'floating' as const,
        variables: {
          borderRadius: (nodeAttributes.inputBorderRadius ?? PRICING_ELEMENT_DEFAULT_INPUT_BORDER_RADIUS) as string,
          focusOutline: 'none',
          focusBoxShadow: 'none',
        },
        inputs: 'condensed' as const,
        rules: appearanceRules,
      },
      mode: 'payment',
      paymentMethodCreation: 'manual',
    };

    if (price.interval === PriceInterval.DONATION) {
      if (donationAmountCents > 0) {
        return {
          ...baseOptions,
          amount: donationAmountCents,
        };
      }

      return {
        ...baseOptions,
        amount: 100, // minimum amount for payment elements
      };
    }

    return baseOptions;
  }, [
    selectedPrice,
    selectedGiftPrice,
    donationAmountCents,
    currentFormMode,
    publication?.language,
    appearanceRules,
    isInternalCheckout,
    nodeAttributes.inputBorderRadius,
  ]);

  const canLoginWithSSO = useMemo(() => {
    if (publication?.has_subscription_password) return false;
    if (isLoggedIn) return false;
    if (currentFormMode === 'gift') return false;
    if (!isInternalCheckout) return false;
    return true;
  }, [isInternalCheckout, isLoggedIn, currentFormMode, publication]);

  return (
    <UpgradeContext.Provider
      value={{
        email,
        setEmail,
        showTaxIdField: isInternalCheckout ? !!publication?.subscriber_tax_id_collection_enabled : false,
        taxId,
        setTaxId,
        donationAmountCents,
        setDonationAmountCents,
        selectedTier,
        setSelectedTier,
        selectedPrice,
        setSelectedPrice,
        selectedGiftTier,
        setSelectedGiftTier,
        selectedGiftPrice,
        setSelectedGiftPrice,
        isStripeLoading,
        stripeSession,
        isInternalCheckout,
        currentFormMode,
        setCurrentFormMode,
        stripeElementsOptions,
        canLoginWithSSO,
      }}
    >
      {children}
    </UpgradeContext.Provider>
  );
};
